import { useParams, Link } from "react-router-dom";
import { Seo, localBusinessLD, serviceLD, faqLD, breadcrumbLD, SITE_URL } from "../lib/seo";
import { SERVICES, serviceBySlug, PROCESS, SITE } from "../data/site";
import { CITIES } from "../data/cities";
import { Reveal, SectionHead } from "../components/Reveal";
import { PageHero, CtaBand } from "../components/Shared";
import NotFound from "./NotFound";

export default function ServicePage() {
    const { slug } = useParams();
    const service = serviceBySlug(slug);
    if (!service) return <NotFound />;

    const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
    const title = `${service.title} à ${SITE.city} — prix & devis 48 h`;

    return (
        <>
            <Seo
                title={service.seo?.title || title}
                description={service.seo?.description || `${service.title} dans l'Oise et le Val-d'Oise : ${service.short} Tarifs indicatifs, exemples de réalisations, devis gratuit sous 48 h. ${SITE.name}, artisan à ${SITE.city}.`}
                path={`/${service.slug}`}
                jsonld={[
                    localBusinessLD(),
                    serviceLD({ name: service.title, description: service.intro, price: service.price }),
                    faqLD(service.faq),
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: service.title, path: `/${service.slug}` },
                    ]),
                ]}
            />
            <PageHero
                img={service.hero}
                crumb={`Rénovation intérieure — Solino Pro`}
                title={service.title}
                serif={service.serif}
            />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.4fr_1fr] gap-14" data-testid="service-intro">
                <Reveal>
                    <p className="font-display italic text-2xl sm:text-3xl text-ivory/90 leading-snug">
                        {service.short}
                    </p>
                    <p className="mt-8 text-base sm:text-lg text-smoke leading-relaxed">{service.intro}</p>
                    <p className="mt-6 text-sm text-smoke leading-relaxed border-l-2 border-gold/40 pl-5" data-testid="service-seo-line">
                        Rénovation intérieure, sols et murs : {service.title.toLowerCase()} s'inscrit dans une prise en
                        charge complète de votre {service.slug.includes("peinture-exterieure") ? "rénovation extérieure" : "rénovation intérieure"} par Solino Pro,
                        artisan BTP à {SITE.city} — de la préparation du support à la dernière finition.
                    </p>
                    <Link
                        to="/devis"
                        className="mt-10 inline-flex items-center rounded-full bg-gold text-ink font-bold px-8 py-4 hover:scale-[1.04] transition-transform duration-300"
                        data-testid="service-intro-cta"
                    >
                        Devis gratuit — {service.price}
                    </Link>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="border border-linew bg-coal p-8" data-testid="service-tarifs">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-6">Tarifs indicatifs</p>
                        <ul className="divide-y divide-white/5">
                            {service.tarifs.map(([label, price]) => (
                                <li key={label} className="py-4 flex flex-col gap-1">
                                    <span className="text-ivory/85 text-sm">{label}</span>
                                    <span className="text-gold font-semibold">{price}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-6 text-xs text-smoke leading-relaxed">
                            Estimations donnant un ordre de grandeur — chaque chantier fait l'objet
                            d'un devis détaillé gratuit, ajusté à votre support et vos finitions.
                        </p>
                    </div>
                </Reveal>
            </section>

            <section className="border-y border-linew bg-coal/60" data-testid="service-prestations">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28">
                    <SectionHead eyebrow="Ce que nous faisons" title="Le périmètre," serif="sans mauvaise surprise." />
                    <div className="mt-12 grid sm:grid-cols-2 gap-px bg-linew border border-linew">
                        {service.prestations.map((p, i) => (
                            <Reveal key={p} delay={i * 0.05}>
                                <div className="flex items-start gap-4 bg-ink p-6 h-full">
                                    <span className="font-display italic text-gold text-2xl leading-none" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="text-ivory/80 text-sm leading-relaxed pt-1">{p}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28" data-testid="service-process">
                <SectionHead eyebrow="Le déroulé" title="Trois étapes," serif="zéro stress." />
                <div className="mt-12 grid md:grid-cols-3 gap-8">
                    {PROCESS.map((p, i) => (
                        <Reveal key={p.step} delay={i * 0.08}>
                            <div className="border-l border-gold/30 pl-6">
                                <span className="text-[11px] uppercase tracking-[0.3em] text-gold">{p.step}</span>
                                <h3 className="mt-3 text-lg text-ivory font-medium">{p.title}</h3>
                                <p className="mt-2 text-sm text-smoke leading-relaxed">{p.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[900px] px-5 sm:px-8 pb-20 lg:pb-28" data-testid="service-faq">
                <SectionHead eyebrow="Questions fréquentes" title="On vous répond" serif="franc-jeu." align="center" />
                <div className="mt-12 space-y-3">
                    {service.faq.map((f, i) => (
                        <Reveal key={f.q} delay={i * 0.06}>
                            <details className="group border border-linew bg-coal" data-testid={`service-faq-item-${i}`}>
                                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 text-ivory/90 text-sm font-medium">
                                    {f.q}
                                    <span className="text-gold group-open:rotate-45 transition-transform duration-300 text-xl" aria-hidden="true">+</span>
                                </summary>
                                <p className="px-5 pb-5 text-sm text-smoke leading-relaxed">{f.a}</p>
                            </details>
                        </Reveal>
                    ))}
                </div>
            </section>

            <CtaBand />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20" data-testid="service-crosslinks">
                <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-8">Autres services</p>
                <div className="grid sm:grid-cols-3 gap-4">
                    {others.map((s) => (
                        <Link key={s.slug} to={`/${s.slug}`} className="group border border-linew p-6 hover:border-gold/60 transition-colors" data-testid={`service-crosslink-${s.slug}`}>
                            <span className="block text-lg text-ivory group-hover:text-gold transition-colors">{s.title}</span>
                            <span className="mt-2 block text-xs text-smoke leading-relaxed">{s.short.slice(0, 80)}…</span>
                        </Link>
                    ))}
                </div>
                <p className="mt-10 text-sm text-smoke">
                    Nous intervenons à {CITIES.map((c) => c.name).join(", ")} et dans tout le secteur —{" "}
                    <Link to="/zones" className="text-gold hover:underline">voir toutes les zones desservies</Link>.
                </p>
            </section>
        </>
    );
}
